import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import bgImg from '../assets/images/bg-guide01.png';
import postImg from '../assets/images/05tale-post.png';
import mainImg from '../assets/images/05tale-main.png';
import titleImg from '../assets/images/05tale-title-txt.png';
import confirmBtnImg from '../assets/images/05tale-confirm-btn.png';
import contractConfirmBtnImg from '../assets/images/03contract-confirm-btn.png';
import failImg from '../assets/images/05tale-fail.png';
import Bookmark from '../components/Bookmark';
import { withUrlValidation } from '../components/withUrlValidation';
import { useSessionNavigation } from '../hooks/useSessionNavigation';
import { soundManager } from '../utils/soundUtils';

const FadeInBackground = styled.div<{ visible: number }>`
  width: 100vw;
  height: 100dvh;
  min-height: 100dvh;
  background: url(${bgImg}) center/cover no-repeat;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  opacity: ${({ visible }) => visible};
  transition: opacity 1.5s ease;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: black;
    opacity: ${({ visible }) => 1 - visible};
    transition: opacity 1.5s ease;
    z-index: -1;
  }
`;

const Title = styled.img<{ visible: number }>`
  width: 85vw;
  max-width: 90vw;
  margin-top: 7dvh;
  margin-bottom: 4dvh;
  opacity: ${({ visible }) => visible};
  transition: opacity 1.5s ease;
`;

const MainWrapper = styled.div<{ visible: number }>`
  position: absolute;
  top: 47%;
  left: 50%;
  transform: translate(-50%, -50%) translateY(${({ visible }) => visible ? '0' : '40px'});
  width: 95dvw;
  max-width: 95dvw;
  aspect-ratio: 52/65;
  opacity: ${({ visible }) => visible};
  transition: opacity 1.2s cubic-bezier(0.4,0,0.2,1), transform 1.2s cubic-bezier(0.4,0,0.2,1);
  pointer-events: ${({ visible }) => visible ? 'auto' : 'none'};
`;



const Post = styled.img.withConfig({
  shouldForwardProp: (prop) => !['visible', 'isAppearing'].includes(prop)
})<{ visible: number; isAppearing: boolean }>`
  width: 90vw;
  max-width: 90vw;
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, ${({ visible, isAppearing }) => 
    isAppearing && !visible ? 'calc(-50% + 100px)' : '-50%'
  });
  opacity: ${({ visible }) => visible};
  transition: ${({ isAppearing }) => 
    isAppearing ? 'all 1.5s cubic-bezier(0.4, 0, 0.2, 1)' : 'opacity 1.5s ease'
  };
  z-index: 25;
  pointer-events: ${({ visible }) => visible ? 'auto' : 'none'};
`;



const Main = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  border-radius: 0.5dvw;
  z-index: 5;
  pointer-events: none;
  
  /* 이미지 로딩 최적화 */
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  backface-visibility: hidden;
  transform: translateZ(0);
`;



const MainConfirmButton = styled.img.withConfig({
  shouldForwardProp: (prop) => !['visible'].includes(prop)
})<{ visible: boolean }>`
  position: absolute;
  top: 85%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 57%;
  cursor: pointer;
  z-index: ${({ visible }) => visible ? 30 : -1};
  opacity: ${({ visible }) => visible ? 1 : 0};
  transition: opacity 1.5s ease;
  pointer-events: ${({ visible }) => visible ? 'auto' : 'none'};
  
  &:hover {
    transform: translate(-50%, -50%) scale(1.05);
    transition: transform 0.2s ease;
  }
  
  &:active {
    transform: translate(-50%, -50%) scale(0.95);
  }
`;

const PostConfirmButton = styled.img.withConfig({
  shouldForwardProp: (prop) => !['visible'].includes(prop)
})<{ visible: boolean }>`
  position: absolute;
  top: 80%;
  left: 50%;
  transform: translate(-50%, 0);
  width: 85dvw;
  margin-top: 5dvh;
  cursor: pointer;
  opacity: ${({ visible }) => visible ? 1 : 0};
  transition: opacity 0.5s ease;
  z-index: ${({ visible }) => visible ? 1 : -1};
  pointer-events: ${({ visible }) => visible ? 'auto' : 'none'};
  
  &:hover {
    transform: translate(-50%, 0) scale(1.05);
    transition: transform 0.2s ease;
  }
  
  &:active {
    transform: translate(-50%, 0) scale(0.95);
  }
`;

const FailPopup = styled.div.withConfig({
  shouldForwardProp: (prop) => !['visible'].includes(prop)
})<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  opacity: ${({ visible }) => visible ? 1 : 0};
  visibility: ${({ visible }) => visible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const TVShutdownOverlay = styled.div<{ isActive: boolean; progress: number }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  pointer-events: none;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: ${props => props.progress * 50}%;
    background: black;
    transition: height 0.8s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: ${props => props.progress * 50}%;
    background: black;
    transition: height 0.8s ease;
  }
`;

const FailImage = styled.img`
  width: 80%;
  max-width: 300px;
  cursor: pointer;
`;

const TaleHousePage: React.FC = () => {
  const { navigateWithSession } = useSessionNavigation();
  const [bgVisible, setBgVisible] = useState(0);
  const [bookmarkVisible] = useState(1); // 북마크를 표시하도록 1로 변경
  const [titleVisible, setTitleVisible] = useState(0);
  const [postVisible, setPostVisible] = useState(0);
  const [postIsAppearing, setPostIsAppearing] = useState(true);
  const [mainVisible, setMainVisible] = useState(0);
  const [mainConfirmButtonVisible, setMainConfirmButtonVisible] = useState(false);
  const [showFailPopup, setShowFailPopup] = useState(false);
  const [postClicked, setPostClicked] = useState(false);
  const [postConfirmButtonVisible, setPostConfirmButtonVisible] = useState(false);
  const [showBookmarkPopup, setShowBookmarkPopup] = useState(false); // 북마크 팝업 상태 추가
  const [showTVShutdown, setShowTVShutdown] = useState(false);
  const [tvShutdownProgress, setTvShutdownProgress] = useState(0);
  const [mainImageLoaded, setMainImageLoaded] = useState(false); // 메인 이미지 로딩 상태 추가

  useEffect(() => {
    // ProgressCard에서 돌아온 경우 북마크 팝업 표시
    const fromProgressCard = localStorage.getItem('fromProgressCard');
    if (fromProgressCard === 'true') {
      setShowBookmarkPopup(true);
      localStorage.removeItem('fromProgressCard');
    }

    // 메인 이미지 프리로딩
    const preloadMainImage = () => {
      const img = new Image();
      img.onload = () => setMainImageLoaded(true);
      img.onerror = () => {
        console.error('메인 이미지 로딩 실패:', mainImg);
        setMainImageLoaded(false);
      };
      img.src = mainImg;
    };
    
    preloadMainImage();
  }, []);


  useEffect(() => {
    // 애니메이션 시퀀스 - 타이틀 먼저, 그 다음 포스트 팝업
    const bgTimer = setTimeout(() => setBgVisible(1), 100);
    const titleTimer = setTimeout(() => setTitleVisible(1), 700);
    const postTimer = setTimeout(() => setPostVisible(1), 1500);
    const postConfirmButtonTimer = setTimeout(() => setPostConfirmButtonVisible(true), 1500);
    
    return () => {
      clearTimeout(bgTimer);
      clearTimeout(titleTimer);
      clearTimeout(postTimer);
      clearTimeout(postConfirmButtonTimer);
    };
  }, []);



  const handlePostConfirmButtonClick = () => {
    if (!postClicked) {
      setPostClicked(true);
      setPostIsAppearing(false);
      setPostVisible(0);
      setPostConfirmButtonVisible(false);
      
      // 메인 이미지가 로드되었는지 확인 후 표시
      const showMainImage = () => {
        if (mainImageLoaded) {
          setMainVisible(1);
          setMainConfirmButtonVisible(true);
        } else {
          // 이미지가 아직 로드되지 않았다면 0.5초 후 다시 시도
          setTimeout(showMainImage, 500);
        }
      };
      
      // 1초 후 메인 이미지 팝업 표시 시도
      setTimeout(showMainImage, 1000);
    }
  };

  const handleMainConfirmButtonClick = () => {
    // 팝업 사라지게 하기
    setMainVisible(0);
    setMainConfirmButtonVisible(false);
    
    // 0.3초 후 TV 셧다운 연출 시작
    setTimeout(() => {
      setShowTVShutdown(true);
      
      // 0.5초 후 위아래에서 검은색이 채워지기 시작
      setTimeout(() => {
        setTvShutdownProgress(1);
      }, 500);
      
      // 1.3초 후 페이지 이동
      setTimeout(() => {
        navigateWithSession('/quiz1');
      }, 1300);
    }, 300);
  };

  const handleFailPopupClick = () => {
    setShowFailPopup(false);
  };

  return (
    <FadeInBackground visible={bgVisible}>
      <Bookmark 
        visible={bookmarkVisible} 
        showPopup={showBookmarkPopup}
        onPopupChange={setShowBookmarkPopup}
      />
      <Title 
        src={titleImg} 
        alt="타이틀" 
        visible={titleVisible} 
      />
      <Post 
        src={postImg} 
        alt="포스트" 
        visible={postVisible}
        isAppearing={postIsAppearing}
      />
      <PostConfirmButton 
        src={confirmBtnImg} 
        alt="포스트 확인 버튼" 
        visible={postConfirmButtonVisible}
        onClick={handlePostConfirmButtonClick}
      />
      <MainWrapper visible={mainVisible}>
        <Main 
          src={mainImg} 
          alt="메인" 
        />
        <MainConfirmButton 
          src={contractConfirmBtnImg} 
          alt="메인 확인 버튼" 
          visible={mainConfirmButtonVisible}
          onClick={handleMainConfirmButtonClick}
        />
      </MainWrapper>
      
      <FailPopup visible={showFailPopup} onClick={handleFailPopupClick}>
        <FailImage 
          src={failImg} 
          alt="실패 팝업" 
          onClick={(e) => e.stopPropagation()}
        />
      </FailPopup>
      
      <TVShutdownOverlay 
        isActive={showTVShutdown} 
        progress={tvShutdownProgress}
      />
      
    </FadeInBackground>
  );
};

export default withUrlValidation(TaleHousePage, {
  requireValidation: true,
  showCountdown: true
}); 